import { predictionsApis } from '@gleamer/apis';
import { Internal, Mapper, OHIF } from '@gleamer/types';
import { DisplaySetService, ServicesManager } from '@ohif/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  useSetCurrentPredictionsKey,
  useSetInitalPredictions,
  useSetPredictions,
  useSetPredictionsLoadFailed,
} from './predictions.hooks';

type UserAuthenticationService = OHIF.UserAuthenticationService;

/** The preview a URL asks for: `predictionImport`, `predictionProduct`, and the study it names; undefined when none. */
export function previewFromSearch(search: string): predictionsApis.PredictionPreview | undefined {
  const params = new URLSearchParams(search);
  const importId = Number(params.get('predictionImport')) || undefined;
  const productId = Number(params.get('predictionProduct')) || undefined;
  if (importId === undefined && productId === undefined) {
    return undefined;
  }
  return { importId, productId, studyInstanceUID: params.get('studyInstanceUID') ?? undefined };
}

/** The series a case shows, in its studies' order: what a task's predictions are read for. */
export function seriesOfItem(item: Internal.LabelTaskItem | null | undefined): string[] {
  return (item?.studies ?? []).flatMap(study =>
    (study.series ?? []).map(series => series.SeriesInstanceUID)
  );
}

/**
 * The predictions on the case's series only: one on a series the viewer did not load is never asked about, so it
 * is never saved with the pass. A server that ignores the series filter is held to it here too.
 */
export function onTheCase<T extends { seriesInstanceUID: string }>(
  predictions: T[],
  seriesInstanceUIDs: string[]
): T[] {
  if (seriesInstanceUIDs.length === 0) {
    return predictions;
  }
  const shown = new Set(seriesInstanceUIDs);
  return predictions.filter(prediction => shown.has(prediction.seriesInstanceUID));
}

/**
 * Where a case's unsaved statuses are kept: one patient's items show different series, so the key names them,
 * hashed, since a case may have many.
 */
export function predictionsKey(
  patientId: string,
  taskId: number,
  seriesInstanceUIDs: string[]
): string {
  let hash = 5381;
  for (const char of predictionsApis.caseSeries(seriesInstanceUIDs).join(',')) {
    hash = ((hash << 5) + hash + char.charCodeAt(0)) | 0;
  }
  return `predictions-${patientId}-${taskId}-${(hash >>> 0).toString(36)}`;
}

export function usePredictionsQuery({
  servicesManager,
  patientId,
  taskId,
  seriesInstanceUIDs = [],
}: {
  servicesManager: ServicesManager;
  patientId: string;
  taskId?: number;
  /** A task's case: the series it shows. */
  seriesInstanceUIDs?: string[];
}) {
  const { userAuthenticationService, displaySetService } =
    servicesManager.services as {
      userAuthenticationService: UserAuthenticationService;
      displaySetService: DisplaySetService;
    };

  const setCurrentPredictionsKey = useSetCurrentPredictionsKey();
  // a task's case: the series its predictions are read for, and that its unsaved statuses are keyed by
  const series = taskId ? predictionsApis.caseSeries(seriesInstanceUIDs) : [];

  if (taskId && patientId) {
    setCurrentPredictionsKey(predictionsKey(patientId, taskId, series));
  }

  const setPredictions = useSetPredictions();
  const setLoadFailed = useSetPredictionsLoadFailed();
  // the preview mode opened from the Prediction imports page: an import's predictions, or a product's current ones
  const preview = taskId
    ? undefined
    : previewFromSearch(window.location.search);

  const queryResult = useQuery({
    queryKey: ['predictions', patientId, taskId, preview, series],
    queryFn: async () => {
      const data = await predictionsApis.fetchPredictions(
        userAuthenticationService,
        patientId,
        taskId,
        preview,
        series
      );
      return {
        ...data,
        predictions: await Promise.all(
          onTheCase(data.predictions, series).map((prediction) => {
            let displaySet = displaySetService
              .getDisplaySetsForSeries(prediction.seriesInstanceUID)
              .find(
                (ds) => ds.StudyInstanceUID === prediction.studyInstanceUID
              );
            // a line of another model than the document's names its own (a file of several models, a product preview)
            prediction.algorithm = prediction.algorithm ?? data.algorithm;
            return Mapper.PredictionMapper.toPredictionInternalWithDisplaySet(
              prediction,
              displaySet?.SeriesInstanceUID
            );
          })
        ),
      };
    },
    enabled: !!patientId,
  });

  const { data, error } = queryResult;

  const setInitialPredictions = useSetInitalPredictions();
  const caseKey = JSON.stringify([patientId, taskId, preview, series]);
  useEffect(() => {
    // a pass saves the predictions as read: another case's must never be saved with this one, while its read is in
    // flight or once it has failed (prev/next is in-app, so the atom outlives the case)
    setInitialPredictions([]);
  }, [caseKey, setInitialPredictions]);

  useEffect(() => {
    // cleared as soon as the query is no longer in error — another case's read, a retry — not only once data comes
    setLoadFailed(!!error);
    if (data) {
      setPredictions({
        predictions: data.predictions,
        overrideInitialPredictions: true,
      });
    }
    // on an error the case's stored statuses are left alone: the flag shows the error and holds Validate, and the
    // reload it asks for reads them back
  }, [data, error]);

  return queryResult;
}
